import {gsap} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {TheCrypt} from "../TheCrypt";
import {TheCryptHelper} from "../utils/TheCryptHelper";
import {TheCryptWinConfig} from "../configs/TheCryptWinConfig";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";
import {TheCryptSoundConfig} from "../configs/TheCryptSoundConfig";

export interface BitmapTextStyleOptions {
    fontName: string;
    fontSize?: number;
    align?: string;
    tint?: number;
}

export class BasicBitmapCountUp extends PIXI.Container {
    public currentCountValue: number;     //Used by greensock. Don't change
    protected _amountText: PIXI.BitmapText;
    protected _anchor: PIXI.Point;
    private _activeAnimation?: gsap.core.Timeline;
    private _prevValue: number;
    private _pareCalculatedScale: boolean;
    private readonly _maxWidth: number | undefined;

    constructor(style: BitmapTextStyleOptions, maxWidth?: number, anchor?: PIXI.Point) {
        super();
        this._maxWidth = maxWidth;
        this._anchor = anchor ? anchor : new PIXI.Point(0.5, 0.5);
        this._amountText = new PIXI.BitmapText("0000000", style);
        this.addChild(this._amountText);
    }

    public stopAt(amount: number) {
        if (this._activeAnimation != undefined) {
            this._activeAnimation.pause();
            this._activeAnimation.kill();
        }
        delete this._activeAnimation;
        this.currentCountValue = amount;
        this._prevValue = amount;
        this.setAmount(amount);
    }

    public preCalculateScale(from: number, to: number): void {
        this.setScaleToFitMaxWidth(from, to);
        this._pareCalculatedScale = true;
    }

    public getCountAnimation(from: number, to: number, duration: number, ease: string | gsap.EaseFunction = "none", bigwinLevel:number = -1): gsap.core.Timeline {
        const winType: string = TheCryptWinConfig.getWinConfig().type;

        const tl: gsap.core.Timeline = gsap.timeline({
            onUpdate: () => this.updateText()
        });
        tl.add(() => {
            if ((winType === TheCryptWinConfig.LARGE || winType === TheCryptWinConfig.MEDIUM)) {
                TheCryptSoundManager.playCountUpStart();
            }
            TheCryptSoundManager.countUpEndSoundPlayed = false;
        },0);
        tl.add(() => this.stopAt(from));
        tl.add(() => {this._activeAnimation = tl});

        if (!this._pareCalculatedScale) {
            tl.add(() => this.setScaleToFitMaxWidth(from, to));
        }

        tl.add(() => this.updateTextLayout());
        tl.add(gsap.to(this, {duration: duration, currentCountValue: to, ease: ease}));
        tl.add(()=>{
            this.setAmount(to);
            if ((winType === TheCryptWinConfig.LARGE || winType === TheCryptWinConfig.MEDIUM)) {
                TheCryptSoundManager.stopSoundEffect("winCountup");
            }
        });
        return tl;
    }

    protected updateText() {
        if (this.currentCountValue - this._prevValue > 0.01) {
            this.setAmount(this.currentCountValue);
            this._prevValue = this.currentCountValue;
        }
    }

    public setAmount(value: number) {
        const newString = SlotGame.apiPlugin.currency.formatValue(value);
        if (newString.length != this._amountText.text.length) {
            this._amountText.text = newString;
            this.updateTextLayout();
        } else {
            this._amountText.text = newString;
        }
    }

    protected updateTextLayout() {
        this._amountText.position.set(-this._amountText.width * this._anchor.x, -this._amountText.height * this._anchor.y);
    }

    protected setScaleToFitMaxWidth(from: number, to: number): void {
        let textScale = 1;
        if (this._maxWidth != undefined) {
            this._amountText.scale.set(1);
            this.setAmount(to);
            textScale = Math.min(this._maxWidth / this._amountText.width, 1);
            this.setAmount(from);
        }
        this._amountText.scale.set(textScale);
    }
}
